import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import DashboardLoadingIcon from '@/components/icon/dashboard-loading-icon'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'

import { useAuth } from '@/hooks/use-auth'
import { useZodResolver } from '@/hooks/use-zod-resolver'
import { SignUpUpdateUserSchema } from '@/schema/auth-schema'
import { authUpdateUserService } from '@/services/auth-service'

interface AccountSecurityNameFormProps {
  onNameChangeSuccess?: () => void
}
const AccountSecurityNameForm = ({ onNameChangeSuccess }: AccountSecurityNameFormProps) => {
  const tFormAuthCommon = useTranslations('Auth.forms.common')
  const tFormCurrent = useTranslations('Settings.forms.account_security_name_form')
  const tFormFields = useTranslations('Settings.forms.account_security_name_form.fields')
  const resolver = useZodResolver({ schema: SignUpUpdateUserSchema, tFormFields })
  const [isChangeName, setIsChangeName] = useState(false)
  const { toast } = useToast()
  const { session, refreshSession } = useAuth()

  const form = useForm<typeof SignUpUpdateUserSchema._type>({
    resolver: resolver,
    defaultValues: {
      name: session?.user?.name ?? '',
    },
  })

  const onSubmit = useCallback(
    async (values: typeof SignUpUpdateUserSchema._type) => {
      const { name } = values

      const { ok } = await authUpdateUserService({ name })

      if (ok) {
        await refreshSession()
        toast({
          variant: 'success',
          description: tFormCurrent('messages.submit_success'),
        })

        form.reset({
          name: values.name,
        })
        setIsChangeName(false)
        onNameChangeSuccess?.()
      } else {
        toast({
          variant: 'error',
          description: tFormAuthCommon('messages.default_error'),
        })
      }
    },
    [form, onNameChangeSuccess, refreshSession, session?.user?.name, toast],
  )

  const onCancel = useCallback(() => {
    setIsChangeName(false)
    form.reset({
      name: session?.user?.name ?? '',
    })
  }, [form, session?.user?.name])

  useEffect(() => {
    form.reset({
      name: session?.user?.name ?? '',
    })
  }, [session?.user?.name])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
        <Label>{tFormFields('name')}</Label>
        <div className="flex items-center justify-between gap-4">
          {isChangeName ? (
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex w-full justify-between gap-4">
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={tFormCurrent('placeholders.name')}
                        autoFocus
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <div className="flex items-center gap-2">
                      <Button
                        type="submit"
                        variant="default"
                        className="whitespace-nowrap"
                        disabled={form.formState.isSubmitting}
                      >
                        {tFormCurrent('actions.form_submit')}
                        {form.formState.isSubmitting && <DashboardLoadingIcon className="ml-2 text-base-muted" />}
                      </Button>
                      <Button
                        variant="outline"
                        className="whitespace-nowrap"
                        onClick={onCancel}
                        disabled={form.formState.isSubmitting}
                      >
                        {tFormCurrent('actions.cancel')}
                      </Button>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <>
              <Input value={session?.user?.name} disabled className={'border-none pl-0'} />
              <Button
                variant="outline"
                className="whitespace-nowrap"
                onClick={() => {
                  setIsChangeName(true)
                }}
                type="button"
              >
                {tFormCurrent('actions.change_name')}
              </Button>
            </>
          )}
        </div>
      </form>
    </Form>
  )
}

AccountSecurityNameForm.displayName = 'AccountSecurityNameForm'

export { AccountSecurityNameForm }
